import { useQuery } from '@tanstack/react-query';
import type { SalesReportingData, RedemptionData, WalletStats } from '@gik/platform-management-api-js';
import { usePlatformManagementClient } from '../../../contexts';
import type { ReactQueryOptions } from '../../../types/react-query';
import { useQueryDefaults } from '../../../client';
import moment from 'moment';

export interface RedemptionQueryParams {
  startDate: string;
  endDate: string;
  filterBySoldTimeframe?: boolean;
  redeemedBefore?: string;
}

/**
 * Configuration options for wallet reporting hooks
 */
export interface WalletReportingHookOptions {
  /**
   * Whether to use mock data instead of making API calls
   * Useful for development and testing when the backend is not available
   */
  useMockData?: boolean;
}

/**
 * Hook for fetching wallet sales report data from the API
 *
 * @param startDate - The start date for the sales report period
 * @param endDate - The end date for the sales report period
 * @param redeemStart - Optional start date for redemption filter
 * @param redeemEnd - Optional end date for redemption filter
 * @param options - Optional React Query configuration options
 * @param hookOptions - Additional options specific to this hook
 * ```
 */
export function useAdminWalletSalesReport(
  startDate?: Date,
  endDate?: Date,
  redeemStart?: Date,
  redeemEnd?: Date,
  options?: ReactQueryOptions<SalesReportingData[]>,
  hookOptions?: WalletReportingHookOptions
) {
  const { client } = usePlatformManagementClient();
  const formattedStartDate = startDate ? moment(new Date(startDate), 'yyyy-MM-dd') : null;
  const formattedEndDate = endDate ? moment(new Date(endDate), 'yyyy-MM-dd') : null;
  const formattedRedeemStart = redeemStart ? moment(new Date(redeemStart), 'yyyy-MM-dd') : null;
  const formattedRedeemEnd = redeemEnd ? moment(new Date(redeemEnd), 'yyyy-MM-dd') : null;
  const useMockData = hookOptions?.useMockData || process.env.NEXT_PUBLIC_USE_MOCK_API === 'true';

  return useQuery({
    ...useQueryDefaults,
    ...options,
    queryKey: [
      'walletReporting',
      'sales',
      formattedStartDate,
      formattedEndDate,
      formattedRedeemStart,
      formattedRedeemEnd,
      useMockData,
    ],
    queryFn: async () => {
      try {
        const response = await client.admin.walletReporting.getSalesReport(
          formattedStartDate?.toISOString(),
          formattedEndDate?.toISOString(),
          formattedRedeemStart?.toISOString(),
          formattedRedeemEnd?.toISOString()
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching sales data:', error);
        throw error;
      }
    },
  });
}

/**
 * Hook for fetching wallet redemption report data from the API
 *
 * @param startDate - The start date for the redemption report period
 * @param endDate - The end date for the redemption report period
 * @param options - Optional React Query configuration options
 * @param hookOptions - Additional options specific to this hook
 */
export function useAdminWalletRedemptionsReport(
  startDate?: Date,
  endDate?: Date,
  options?: ReactQueryOptions<RedemptionData[]>,
  hookOptions?: WalletReportingHookOptions
) {
  const { client } = usePlatformManagementClient();
  const formattedStartDate = startDate ? moment(new Date(startDate), 'yyyy-MM-dd') : undefined;
  const formattedEndDate = endDate ? moment(new Date(endDate), 'yyyy-MM-dd') : undefined;
  const useMockData = hookOptions?.useMockData || process.env.NEXT_PUBLIC_USE_MOCK_API === 'true';

  return useQuery({
    ...useQueryDefaults,
    ...options,
    queryKey: ['walletReporting', 'redemptions', formattedStartDate, formattedEndDate, useMockData],
    queryFn: async () => {
      try {
        const response = await client.admin.walletReporting.getRedemptionsReport(
          formattedStartDate?.toISOString(),
          formattedEndDate?.toISOString()
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching redemption data:', error);
        throw error;
      }
    },
  });
}

export function useAdminWalletStats(options?: ReactQueryOptions<WalletStats>) {
  const { client } = usePlatformManagementClient();

  return useQuery({
    ...useQueryDefaults,
    ...options,
    queryKey: ['walletReporting', 'stats'],
    queryFn: async () => {
      try {
        const response = await client.admin.walletReporting.getStats();
        return response.data;
      } catch (error) {
        console.error('Error fetching redemption data:', error);
        throw error;
      }
    },
  });
}
